<script setup lang="ts">
import { PromotionType } from '@rediredi/types'
import { useFlag } from '@unleash/proxy-client-vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import { FALLBACK_IMAGE_PATH } from '~/consts/images'
import { IStorefrontItem } from '~/types/item'

import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps<{
  item: IStorefrontItem
}>()

const emit = defineEmits<{
  (e: 'view-product', item: IStorefrontItem): void
  (e: 'add-product', item: IStorefrontItem): void
}>()

const modules = [Pagination]
const { event } = useEvent()
const theme = useStoreTheme()
const isMobile = useIsMobile()
const globalStore = useGlobalStore()
const discountStore = useDiscountStore()
const isItemGridCarouselFlagEnabled = useFlag('storefront.item-grid-carousel')

const handleClick = () => {
  emit('view-product', props.item)
  event('select_item', {
    item_list_id: 'main_search',
    item_list_name: 'Main search',
    items: [itemToAnalytics(props.item)],
  })
}

const isVariationPriceEnabled = useFlag('storefront.variation-price')

const currency = globalStore.storeCurrency

const oldPrice = computed(() => {
  if (
    !props.item.promotion?.showBeforeAndAfterPrice &&
    (!discountStore.coupon ||
      [PromotionType.FreeShipping, PromotionType.SellingPrice].includes(discountStore.coupon.type))
  )
    return null

  if (isVariationPriceEnabled.value) {
    const cover = getCoverVariant(props.item)
    return cover.price
  }

  return props.item.baseVariant.price
})

const price = computed(() => {
  if (isVariationPriceEnabled.value) {
    const cover = getCoverVariant(props.item)

    if (props.item.promotion && props.item.promotion.type !== PromotionType.Label) {
      return discountStore.getDiscountedPrice(cover.promotionPrice!)
    } else {
      return discountStore.getDiscountedPrice(cover.price!)
    }
  }

  return props.item.promotion && props.item.promotion.type !== PromotionType.Label
    ? discountStore.getDiscountedPrice(props.item.baseVariant.promotionPrice!)
    : discountStore.getDiscountedPrice(props.item.baseVariant.price!)
})

// Get all pictures for the product
const pictures = computed(() => {
  const allPictures = getCoverVariantPictures(props.item)
  return allPictures.length > 0 ? allPictures : [FALLBACK_IMAGE_PATH]
})

const imgSrc = computed(() => {
  const pics = getCoverVariantPictures(props.item)
  if (pics.length) return optimizedImage(getPictureUrl(pics[0]), 'small')
  return FALLBACK_IMAGE_PATH
})

const redirect = () => {
  useItemRedirect(props.item)
}

const isOutOfStock = computed(() => (props.item.quantityAvailable || 0) < 1)
const shouldShowCarousel = computed(
  () => pictures.value.length > 1 && isMobile.value && isItemGridCarouselFlagEnabled.value,
)
</script>

<template>
  <div
    class="relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-2xl bg-white p-3 transition-all duration-300 hover:scale-[100.5%] hover:shadow-xl lg:p-5"
  >
    <RProductIndicator
      v-if="isOutOfStock || props.item.promotion"
      :type="isOutOfStock ? 'outOfStock' : 'promotion'"
      :promotion="props.item.promotion"
      context="grid"
    />

    <div role="button" class="h-full" @click="handleClick">
      <div v-if="shouldShowCarousel" class="aspect-square w-full">
        <Swiper
          :modules="modules"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 3,
          }"
          :space-between="0"
          :slides-per-view="1"
          :grab-cursor="true"
          :touch-ratio="1"
          :touch-angle="45"
          :threshold="5"
          class="swiper-product-grid h-full rounded-2xl"
          @slide-change="event('product_item_cell_grid_image_swipe')"
        >
          <SwiperSlide v-for="(picture, index) in pictures" :key="index">
            <img
              alt=""
              loading="lazy"
              class="flex aspect-square w-full justify-center rounded-2xl object-contain align-middle"
              :class="{
                'p-8 sm:p-16': picture === FALLBACK_IMAGE_PATH,
              }"
              :src="
                picture === FALLBACK_IMAGE_PATH
                  ? picture
                  : optimizedImage(getPictureUrl(picture), 'small')
              "
            />
          </SwiperSlide>
        </Swiper>
      </div>

      <!-- Single image fallback for desktop or single image -->
      <img
        v-else
        alt=""
        loading="lazy"
        class="flex aspect-square w-full justify-center rounded-2xl object-contain align-middle"
        :class="{
          'p-8 sm:p-16': imgSrc === FALLBACK_IMAGE_PATH,
        }"
        :src="imgSrc"
      />

      <div class="flex flex-col gap-2 px-0 py-3 lg:pb-2 lg:pt-4">
        <p
          class="line-clamp-2 h-10 overflow-hidden text-ellipsis text-sm font-medium text-gray-400"
        >
          {{ item.baseVariant.title }}
        </p>

        <div class="flex h-12 flex-col justify-end justify-items-end text-gray-400">
          <template v-if="price?.amount">
            <template v-if="oldPrice">
              <div
                class="h-6 w-full text-[12px] font-medium !leading-6 text-gray-400 line-through lg:text-sm"
              >
                <span>{{ formatPriceInParts(oldPrice.amount, { currency }).currency }}</span>
                <span>{{ formatPriceInParts(oldPrice.amount, { currency }).amount }}</span>
              </div>
            </template>
            <div class="h-6 w-full self-end text-xl font-extrabold !leading-6 lg:text-2xl">
              <span>{{ formatPriceInParts(price.amount, { currency }).currency }}</span>
              <span>{{ formatPriceInParts(price.amount, { currency }).amount }}</span>
            </div>
          </template>
          <template v-else>
            <div class="w-full">
              <span v-if="props.item.promotion" class="text-xl">&nbsp;</span>
              <span class="text-base">
                {{ $t('globals.noPrice') }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <button
      v-if="!globalStore.isCheckoutEnabled || isOutOfStock"
      class="m-auto flex w-full items-center justify-center gap-2 rounded-full bg-whatsapp p-2 px-3 py-3 text-xs font-semibold uppercase md:text-sm lg:px-8"
      :style="{ color: theme.text }"
      @click="redirect"
    >
      {{ isOutOfStock ? $t('components.buyButton.inquire') : $t('components.buyButton.label') }}
      <RIcon size="16" name="mdi:whatsapp" />
    </button>

    <RCartAddButton v-else :item="props.item" text-only @redirect="handleClick" />

    <button
      class="m-auto mt-3 p-0 text-xs font-semibold uppercase md:text-sm lg:mt-4"
      :style="{ color: theme.primary }"
      @click="handleClick"
    >
      {{ $t('components.productCell.secondaryButton.label') }}
    </button>
  </div>
</template>

<style scoped>
/* Custom styles for product grid swiper */
.swiper-product-grid {
  --swiper-pagination-color: v-bind('theme.primary');
  --swiper-pagination-bullet-inactive-color: rgba(255, 255, 255, 0.5);
  --swiper-pagination-bullet-size: 6px;
  --swiper-pagination-bullet-horizontal-gap: 3px;
}

.swiper-product-grid :deep(.swiper-pagination) {
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
}

.swiper-product-grid :deep(.swiper-pagination-bullet) {
  opacity: 0.6;
  transition: all 0.2s ease;
  background: rgba(0, 0, 0, 0.3);
}

.swiper-product-grid :deep(.swiper-pagination-bullet-active) {
  opacity: 1;
  background: v-bind('theme.primary');
}

/* Ensure touch area is sufficient on mobile */
.swiper-product-grid :deep(.swiper-pagination-bullet) {
  width: 8px;
  height: 8px;
  margin: 0 2px;
}

/* Handle dynamic bullets */
.swiper-product-grid :deep(.swiper-pagination-bullet-active-main) {
  opacity: 1;
}

.swiper-product-grid :deep(.swiper-pagination-bullet-active-prev),
.swiper-product-grid :deep(.swiper-pagination-bullet-active-next) {
  opacity: 0.8;
}

.swiper-product-grid :deep(.swiper-pagination-bullet-active-prev-prev),
.swiper-product-grid :deep(.swiper-pagination-bullet-active-next-next) {
  opacity: 0.4;
}
</style>
